/**
 * HVACLayers - Layer control panel for HVAC elements
 * Allows toggling visibility, locking, and opacity of different HVAC layers
 */

import React from 'react';
import { FloatingPanel } from '../ui/Panel';

const HVAC_LAYERS = [
  { id: 'supplyDucts', label: 'Supply Ducts', color: '#e65100' },
  { id: 'returnDucts', label: 'Return Ducts', color: '#1565c0' },
  { id: 'supplyVents', label: 'Supply Vents', color: '#ff7043' },
  { id: 'returnGrilles', label: 'Return Grilles', color: '#42a5f5' },
  { id: 'equipment', label: 'Equipment', color: '#66bb6a' },
  { id: 'fittings', label: 'Fittings', color: '#ab47bc' },
  { id: 'cfmLabels', label: 'CFM Labels', color: '#78909c' },
  { id: 'roomDataLabels', label: 'Room Data Labels', color: '#26a69a' },
];

// Icons
const EyeIcon = ({ visible }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {visible ? (
      <>
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
      </>
    ) : (
      <>
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    )}
  </svg>
);

const LockIcon = ({ locked }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    {locked ? (
      <>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </>
    ) : (
      <>
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 9.9-1" />
      </>
    )}
  </svg>
);

export default function HVACLayers({
  layers,
  onUpdateLayer,
  onClose,
  isMobile = false,
}) {
  // Default layer state if not provided
  const getLayerState = (layerId) => {
    return layers?.[layerId] || { visible: true, locked: false, opacity: 1 };
  };

  const handleToggleVisible = (layerId) => {
    const current = getLayerState(layerId);
    onUpdateLayer(layerId, { ...current, visible: !current.visible });
  };

  const handleToggleLocked = (layerId) => {
    const current = getLayerState(layerId);
    onUpdateLayer(layerId, { ...current, locked: !current.locked });
  };

  const handleOpacityChange = (layerId, opacity) => {
    const current = getLayerState(layerId);
    onUpdateLayer(layerId, { ...current, opacity: parseFloat(opacity) });
  };

  const handleShowAll = () => {
    HVAC_LAYERS.forEach(layer => {
      onUpdateLayer(layer.id, { ...getLayerState(layer.id), visible: true });
    });
  };

  const handleHideAll = () => {
    HVAC_LAYERS.forEach(layer => {
      onUpdateLayer(layer.id, { ...getLayerState(layer.id), visible: false });
    });
  };

  const handleUnlockAll = () => {
    HVAC_LAYERS.forEach(layer => {
      onUpdateLayer(layer.id, { ...getLayerState(layer.id), locked: false });
    });
  };

  return (
    <FloatingPanel
      title="HVAC Layers"
      onClose={onClose}
      position={isMobile ? 'bottom' : 'left'}
      width={isMobile ? '100%' : 260}
    >
      {/* Quick actions */}
      <div style={styles.quickActions}>
        <button onClick={handleShowAll} style={styles.quickBtn}>Show All</button>
        <button onClick={handleHideAll} style={styles.quickBtn}>Hide All</button>
        <button onClick={handleUnlockAll} style={styles.quickBtn}>Unlock All</button>
      </div>

      {/* Layer list */}
      <div style={styles.layerList}>
        {HVAC_LAYERS.map(layer => {
          const state = getLayerState(layer.id);
          return (
            <div key={layer.id} style={styles.layerRow}>
              {/* Color indicator */}
              <div
                style={{
                  ...styles.colorIndicator,
                  backgroundColor: layer.color,
                  opacity: state.visible ? state.opacity : 0.3,
                }}
              />

              {/* Layer name */}
              <span
                style={{
                  ...styles.layerName,
                  opacity: state.visible ? 1 : 0.5,
                  textDecoration: state.locked ? 'none' : 'none',
                }}
              >
                {layer.label}
              </span>

              {/* Visibility toggle */}
              <button
                onClick={() => handleToggleVisible(layer.id)}
                style={{
                  ...styles.iconBtn,
                  color: state.visible ? '#4caf50' : '#666',
                }}
                title={state.visible ? 'Hide layer' : 'Show layer'}
              >
                <EyeIcon visible={state.visible} />
              </button>

              {/* Lock toggle */}
              <button
                onClick={() => handleToggleLocked(layer.id)}
                style={{
                  ...styles.iconBtn,
                  color: state.locked ? '#ff9800' : '#666',
                }}
                title={state.locked ? 'Unlock layer' : 'Lock layer'}
              >
                <LockIcon locked={state.locked} />
              </button>

              {/* Opacity slider */}
              <input
                type="range"
                min="0.1"
                max="1"
                step="0.1"
                value={state.opacity}
                onChange={(e) => handleOpacityChange(layer.id, e.target.value)}
                style={styles.opacitySlider}
                title={`Opacity: ${Math.round(state.opacity * 100)}%`}
              />
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendTitle}>Legend</div>
        <div style={styles.legendRow}>
          <span style={{ ...styles.legendColor, backgroundColor: '#e65100' }} />
          <span>Supply (heating/cooling delivery)</span>
        </div>
        <div style={styles.legendRow}>
          <span style={{ ...styles.legendColor, backgroundColor: '#1565c0' }} />
          <span>Return (air return to system)</span>
        </div>
      </div>
    </FloatingPanel>
  );
}

const styles = {
  quickActions: {
    display: 'flex',
    gap: '6px',
    padding: '8px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  quickBtn: {
    flex: 1,
    padding: '6px 8px',
    fontSize: '11px',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '4px',
    color: '#ccc',
    cursor: 'pointer',
  },
  layerList: {
    padding: '8px 0',
  },
  layerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 12px',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  },
  colorIndicator: {
    width: '12px',
    height: '12px',
    borderRadius: '2px',
    flexShrink: 0,
  },
  layerName: {
    flex: 1,
    fontSize: '12px',
    color: '#e0e0e0',
  },
  iconBtn: {
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '4px',
  },
  opacitySlider: {
    width: '50px',
    height: '4px',
    cursor: 'pointer',
    accentColor: '#00c8ff',
  },
  legend: {
    padding: '12px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
    marginTop: '8px',
  },
  legendTitle: {
    fontSize: '11px',
    color: '#888',
    marginBottom: '8px',
    textTransform: 'uppercase',
  },
  legendRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '11px',
    color: '#aaa',
    marginBottom: '4px',
  },
  legendColor: {
    width: '10px',
    height: '10px',
    borderRadius: '2px',
  },
};
